/**
 * Транзиентные ошибки: сеть оборвалась, чанк не доехал, запрос отменили.
 *
 * Отличать их от настоящих поломок нужно там, где решается, стоит ли слать событие в Sentry.
 * Когда страницу закрывают, браузер рвёт все текущие запросы разом: XHR отдаёт status 0
 * («Network Error»), незавершённый import() — «Importing a module script failed». Один
 * закрытый webview превращается в три-четыре события об ошибке, в которых нет ни бага,
 * ни действия — только шум, за которым не видно настоящих отказов.
 */

/**
 * Ошибка загрузки чанка, а не поломка внутри кода. Формулировки у браузеров разные:
 * Safari — «Importing a module script failed», Chrome — «Failed to fetch dynamically imported
 * module», Firefox — «error loading dynamically imported module». Плюс CSS-препрелоад Vite.
 */
const CHUNK_ERROR_PATTERNS = [
  "importing a module script failed",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "unable to preload css",
];

/** Коды axios для отказов, где ответа не было вовсе (см. AxiosError.ERR_NETWORK и т.д.). */
const TRANSIENT_AXIOS_CODES = new Set(["ERR_NETWORK", "ERR_CANCELED", "ECONNABORTED", "ETIMEDOUT"]);

/** Те же отказы по тексту: у части сборок axios код не доезжает до обработчика. */
const TRANSIENT_MESSAGE_PATTERNS = ["network error", "timeout exceeded", "timeout of", "load failed", "request aborted", "canceled"];

const readMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error ?? "")).toLowerCase();

export const isChunkLoadError = (error: unknown): boolean => {
  const message = readMessage(error);
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/** Отказ сети без ответа сервера: у axios есть code, но нет response. */
const isNetworkAxiosError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const axiosLike = error as { code?: unknown; response?: unknown; isAxiosError?: unknown };
  if (axiosLike.response) {
    return false;
  }
  return typeof axiosLike.code === "string" && TRANSIENT_AXIOS_CODES.has(axiosLike.code);
};

export const isTransientNetworkError = (error: unknown): boolean => {
  if (isChunkLoadError(error) || isNetworkAxiosError(error)) {
    return true;
  }
  const message = readMessage(error);
  return TRANSIENT_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern));
};
